import React, { useState } from 'react'

interface TooltipProps {
    /** Definition / description text shown in the tooltip bubble. */
    content: string
    /** Stable id used to wire aria-describedby between trigger and bubble. */
    id: string
    /** The trigger element (e.g. the existing tab <button>). */
    children: React.ReactElement
}

export function Tooltip({ content, id, children }: Readonly<TooltipProps>): JSX.Element {
    const [open, setOpen] = useState(false)

    const handleMouseEnter = (e: React.MouseEvent) => {
        setOpen(true)
        children.props.onMouseEnter?.(e)
    }

    const handleMouseLeave = (e: React.MouseEvent) => {
        setOpen(false)
        children.props.onMouseLeave?.(e)
    }

    const handleFocus = (e: React.FocusEvent) => {
        setOpen(true)
        children.props.onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent) => {
        setOpen(false)
        children.props.onBlur?.(e)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && open) {
            setOpen(false)
        }
        children.props.onKeyDown?.(e)
    }

    const handleClick = (e: React.MouseEvent) => {
        setOpen(false)
        children.props.onClick?.(e)
    }

    // Merge any existing aria-describedby on the child with the tooltip id (only while open),
    // so we augment rather than overwrite descriptions the trigger already had.
    const mergedDescribedBy = [children.props['aria-describedby'], open ? id : null]
        .filter(Boolean)
        .join(' ') || undefined

    const clonedChild = React.cloneElement(children, {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
        onClick: handleClick,
        ...(mergedDescribedBy ? { 'aria-describedby': mergedDescribedBy } : {}),
    })

    const bubbleStyle: React.CSSProperties = {
        position: 'absolute',
        top: '100%',
        left: 0,
        marginTop: 'var(--space-1)',
        zIndex: 50,
        // Stay within the viewport: cap at 280px but shrink on narrow windows so
        // the bubble never overflows the browser edge (Req 6.2/6.3).
        width: 'max-content',
        maxWidth: 'min(280px, calc(100vw - var(--space-3) - var(--space-3)))',
        padding: 'var(--space-2) var(--space-3)',
        background: 'var(--surface)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-s)',
        fontSize: 13,
        lineHeight: 1.5,
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    }

    return (
        <span style={{ position: 'relative', display: 'inline-flex' }}>
            {clonedChild}
            {open && (
                <div role="tooltip" id={id} style={bubbleStyle}>
                    {content}
                </div>
            )}
        </span>
    )
}
